import React, { useState, useEffect } from 'react';
import { X, QrCode, CreditCard, DollarSign, CheckCircle2, Copy, Check, ShieldCheck, Globe, ExternalLink } from 'lucide-react';
import { PaymentGateway, PaymentDetails, CustomCharge } from '../types';
import { generatePixData, getEffectiveCurrencyRates } from '../lib/shippingAndPayment';
import { getSavedPaymentGatewayConfigs } from './PaymentMethodsModal';

interface CustomChargePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  charge: CustomCharge;
  onPaymentComplete: (chargeId: string, paymentDetails: PaymentDetails) => Promise<void> | void;
}

export const CustomChargePaymentModal: React.FC<CustomChargePaymentModalProps> = ({
  isOpen,
  onClose,
  charge,
  onPaymentComplete
}) => {

  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway>('PIX');
  const [copiedPix, setCopiedPix] = useState(false);
  const [copiedWise, setCopiedWise] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [savedGatewayConfigs, setSavedGatewayConfigs] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      setSavedGatewayConfigs(getSavedPaymentGatewayConfigs());
    }
  }, [isOpen]);


  const rates = getEffectiveCurrencyRates();
  const amountBrl = charge.amountJpy * rates.jpyToBrl;

  // Pix Logic
  const defaultPixData = generatePixData(charge.id, amountBrl, "Egami Corp");
  const pixData = {
    ...defaultPixData,
    pixCopiaECola: savedGatewayConfigs.PIX?.pixCopiaECola || defaultPixData.pixCopiaECola
  };

  const handleCopyPix = () => {
    if (pixData.pixCopiaECola) {
      navigator.clipboard.writeText(pixData.pixCopiaECola);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    }
  };

  const handleCopyWise = () => {
    const email = savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com';
    navigator.clipboard.writeText(email);
    setCopiedWise(true);
    setTimeout(() => setCopiedWise(false), 2000);
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      await onPaymentComplete(charge.id, {
        method: selectedGateway,
        amountTotalBrl: amountBrl,
        gatewayFeeBrl: 0,
        exchangeRate: rates.jpyToBrl,
        gatewayTransactionId: 'simulated-' + Date.now(),
        gatewayReceiptUrl: 'https://example.com/receipt',
        paidAt: new Date().toISOString()
      });
      setPaid(true);
      setTimeout(() => {
        onClose();
        setPaid(false);
      }, 2000);
    } catch (error) {
      console.error(error);
      alert('Erro ao confirmar pagamento');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (paid) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-emerald-600 animate-in fade-in duration-300">
        <div className="text-center text-white p-8">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-short shadow-2xl">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black mb-2 tracking-tight">Pagamento Confirmado!</h2>
          <p className="text-emerald-100 font-medium">Ordem de pagamento quitada com sucesso.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full sm:max-w-xl min-h-screen sm:min-h-0 sm:rounded-3xl shadow-2xl flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 pt-10 sm:pt-6 pb-8 relative shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex flex-col items-center text-center mt-2">
            <span className="px-3 py-1 bg-amber-500 text-amber-950 text-[10px] font-black uppercase tracking-widest rounded-full mb-3 shadow-sm border border-amber-400">
              Pagamento Avulso
            </span>
            <h2 className="text-2xl font-black text-white mb-2">{charge.title}</h2>
            <div className="text-5xl font-black text-white tracking-tighter tabular-nums drop-shadow-md">
              <span className="text-2xl text-emerald-400 font-bold mr-1 align-top">R$</span>
              {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-sm font-medium text-slate-400 mt-2">
              Equivalente a ¥ {charge.amountJpy.toLocaleString('ja-JP')}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50 flex flex-col">
          <div className="mb-6 grid grid-cols-3 gap-2">
            <button
              onClick={() => setSelectedGateway('PIX')}
              className={`p-3 border rounded-xl flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                selectedGateway === 'PIX' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-md ring-1 ring-emerald-500' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <QrCode className="w-5 h-5" />
              <span className="font-bold text-xs">PIX</span>
            </button>
            <button
              onClick={() => setSelectedGateway('Wise')}
              className={`p-3 border rounded-xl flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                selectedGateway === 'Wise' ? 'bg-[#9FE870]/20 border-[#163300] text-[#163300] shadow-md ring-1 ring-[#163300]' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <Globe className="w-5 h-5 text-[#163300]" />
              <span className="font-bold text-xs">Wise</span>
            </button>
            <button
              onClick={() => setSelectedGateway('Stripe')}
              className={`p-3 border rounded-xl flex flex-col items-center justify-center gap-2 transition-all cursor-pointer ${
                selectedGateway === 'Stripe' ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-md ring-1 ring-blue-500' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <CreditCard className="w-5 h-5" />
              <span className="font-bold text-xs">Cartão</span>
            </button>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm grow flex flex-col">
            {selectedGateway === 'PIX' && (
              <div className="text-center space-y-4">
                <div className="p-3 bg-slate-50 rounded-xl inline-block border border-slate-200">
                  <img src={savedGatewayConfigs.PIX?.pixQrCodeImage || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixData.pixCopiaECola)}`} alt="QR Code PIX" className="w-48 h-48 mx-auto mix-blend-multiply" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-2">Pix Copia e Cola</p>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={pixData.pixCopiaECola} className="w-full text-xs font-mono p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 outline-none" />
                    <button onClick={handleCopyPix} className="p-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-colors cursor-pointer shrink-0">
                      {copiedPix ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedGateway === 'Wise' && (
              <div className="space-y-4">
                <div className="p-3 bg-[#163300] text-[#9FE870] rounded-xl flex items-center justify-between gap-2">
                  <span className="text-xs font-bold">Não tem conta na Wise?</span>
                  <a
                    href={savedGatewayConfigs.Wise?.wiseInviteUrl || 'https://wise.com/invite/ilpc/welingtone'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-[#9FE870] hover:bg-[#8edb5f] text-[#163300] font-black text-xs rounded-lg flex items-center gap-1 shrink-0"
                  >
                    <span>Crie sua conta na Wise</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <span className="text-xs text-slate-500 font-bold uppercase block">Conta / E-mail de Destino Wise:</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-mono font-bold text-slate-900 truncate">
                      {savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com'}
                    </span>
                    <button onClick={handleCopyWise} className="px-3 py-1.5 bg-[#163300] text-[#9FE870] rounded-lg font-bold text-xs flex items-center gap-1 cursor-pointer">
                      {copiedWise ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedWise ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Titular: <strong>{savedGatewayConfigs.Wise?.wiseAccountName || 'Egami Corporation'}</strong>
                  </p>
                </div>
              </div>
            )}

            {selectedGateway === 'Stripe' && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <DollarSign className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="font-bold text-slate-800 mb-1">Pagamento com Cartão</h3>
                <p className="text-sm text-slate-500">Integração simulada para teste.</p>
              </div>
            )}
          </div>
          
          <div className="mt-6 pt-4 border-t border-slate-200">
             <button
              onClick={handleConfirmPayment}
              disabled={loading}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  <span>Confirmar Pagamento</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
